import { ValidationAlertsStack } from "@components/ValidationAlertWithBullets";
import { Box, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useFormContext, UseFormReturn } from "react-hook-form";
import { TaxRatesEntity } from "src/@types/catalog/taxRates";
import type { IFormEngine, IFormUIBlock, TFormUISlot } from "../core/types";
import { useCommonStyles } from "../hooks/useCommonStyles";
import { AdditionalOptions } from "./AdditionalOptions";
import { CustomerInformation } from "./customer/CustomerInformation";
import { DocumentInformation } from "./document/DocumentInformation";
import { LineItems } from "./items/LineItems";
import { ManualDocumentActions } from "./ManualDocumentActions";
import { RecurringInvoice } from "./RecurringInvoice";
import { TotalBreakdown } from "./TotalBreakdown";
import { VendorInformation } from "./vendor/VendorInformation";

interface IManualDocumentFormProps {
  translate: (text: any, options?: any) => string;
  taxRates: TaxRatesEntity[] | null;
  engine: IFormEngine;
  uiBlocks?: IFormUIBlock[];
  isSubmitting: boolean;
  isSubmittingNew: boolean;
  isSubmittingDraft: boolean;
  isLoading: boolean;
  isLegalRepValid: boolean;
  validationError?: string[];
  validationErrorActions?: React.ReactNode[];
  onSubmit: () => void;
  onSubmitAndCreateNew: () => void;
  onSaveAsDraft: (values: any) => Promise<void>;
  methods: UseFormReturn<any>;
}

export const ManualDocumentForm = (props: IManualDocumentFormProps) => {
  const {
    translate,
    taxRates,
    engine,
    uiBlocks,
    isSubmitting,
    isSubmittingNew,
    isSubmittingDraft,
    isLoading,
    isLegalRepValid,
    validationError,
    validationErrorActions,
    onSubmit,
    onSubmitAndCreateNew,
    onSaveAsDraft,
    methods,
  } = props;
  const { formContainer } = useCommonStyles();
  const { getValues } = useFormContext<any>();
  const { isFormReset } = engine;
  const { customerCompanyName: isCustomerInfoAdded, RecurringDocument } =
    getValues();
  const [isRecurring, setIsRecurring] = useState(!!RecurringDocument.startDate);
  const hiddenSections = new Set(engine.config.hiddenSections ?? []);
  const isVisible = (section: string) => !hiddenSections.has(section as any);
  const selectedComplianceStandard = ["VeriFactu", "SII", "SDI"].includes(
    engine.config.taxRatesChannel ?? "",
  )
    ? engine.config.taxRatesChannel
    : null;

  const isPOorDN =
    engine.config.documentClass === "PurchaseOrder" ||
    engine.config.documentClass === "DeliveryNote";

  const isReferenceDocument =
    engine.config.documentClass === "CreditNote" ||
    engine.config.documentClass === "DebitNote" ||
    engine.config.invoiceType === "correctiveInvoice";

  const renderBlocks = (slot: TFormUISlot) => {
    const blocks = (uiBlocks ?? [])
      .filter((b) => b.slot === slot)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (blocks.length === 0) return null;

    return blocks.map((block) => (
      <Grid
        key={block.id}
        item
        xs={block.grid?.xs ?? 12}
        sm={block.grid?.sm}
        md={block.grid?.md}
        lg={block.grid?.lg}
        xl={block.grid?.xl}
      >
        {block.render({ translate, engine })}
      </Grid>
    ));
  };

  const customerGateSx = {
    position: "relative",
    opacity: !!isCustomerInfoAdded ? 1 : 0.5,
    pointerEvents: !!isCustomerInfoAdded ? "auto" : "none",
    transition: "opacity 0.2s ease",
  } as const;

  useEffect(() => {
    if (isFormReset > 0) {
      setIsRecurring(false);
    }
  }, [isFormReset]);

  return (
    <>
      <Box sx={formContainer}>
        <Grid container spacing={3}>
          {validationError && validationError.length > 0 && (
            <Grid item xs={12}>
              <ValidationAlertsStack
                items={validationError.map((msg, i) => ({
                  message: msg,
                  action: validationErrorActions?.[i],
                }))}
              />
            </Grid>
          )}

          {isPOorDN ? (
            <>
              {renderBlocks("before_vendor")}
              {isVisible("customer") && (
                <Grid item xs={12}>
                  <CustomerInformation translate={translate} engine={engine} />
                </Grid>
              )}
              {renderBlocks("after_vendor")}
              {isVisible("document") && (
                <Grid item xs={12}>
                  <DocumentInformation translate={translate} engine={engine} />
                </Grid>
              )}
              {renderBlocks("after_document")}
              {isVisible("vendor") && (
                <Grid item xs={12}>
                  <VendorInformation translate={translate} engine={engine} />
                </Grid>
              )}
            </>
          ) : (
            <>
              {renderBlocks("before_vendor")}
              {isVisible("vendor") && (
                <Grid item xs={12}>
                  <VendorInformation translate={translate} engine={engine} />
                </Grid>
              )}
              {renderBlocks("after_vendor")}
              {isVisible("document") && (
                <Grid item xs={12}>
                  <DocumentInformation translate={translate} engine={engine} />
                </Grid>
              )}
              {renderBlocks("after_document")}
              {isVisible("customer") && (
                <Grid item xs={12}>
                  <CustomerInformation translate={translate} engine={engine} />
                </Grid>
              )}
            </>
          )}

          {renderBlocks("after_customer")}

          <Grid item xs={12}>
            <Box sx={customerGateSx}>
              <Grid container spacing={3}>
                {renderBlocks("before_items")}
                {isVisible("items") && (
                  <Grid item xs={12}>
                    <LineItems
                      translate={translate}
                      taxRates={taxRates}
                      onTaxesRefresh={engine.handleFetchTaxRates}
                      selectedComplianceStandard={selectedComplianceStandard}
                    />
                  </Grid>
                )}
                {renderBlocks("after_items")}
                {isVisible("additionalOptions") && (
                  <Grid item xs={12}>
                    <AdditionalOptions
                      translate={translate}
                      taxRates={taxRates}
                      taxes={engine.taxes}
                      onTaxesRefresh={engine.handleFetchTaxRates}
                      selectedComplianceStandard={selectedComplianceStandard}
                      hiddenOptions={
                        engine.config.sections?.additionalOptions?.hiddenOptions
                      }
                      isFormReset={engine.isFormReset}
                    />
                  </Grid>
                )}
                {renderBlocks("after_additional_options")}
                {renderBlocks("before_totals")}
                {isVisible("totals") && (
                  <Grid item xs={12}>
                    <TotalBreakdown translate={translate} />
                  </Grid>
                )}
                {renderBlocks("after_totals")}
                {isVisible("recurring") && isRecurring && (
                  <Grid item xs={12}>
                    <RecurringInvoice translate={translate} />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {isVisible("actions") && (
        <Box sx={{ mt: 3 }}>
          <Box sx={customerGateSx}>
            <Grid container spacing={3}>
              {renderBlocks("before_actions")}
              <Grid item xs={12}>
                <ManualDocumentActions
                  translate={translate}
                  isSubmitting={isSubmitting}
                  isSubmittingNew={isSubmittingNew}
                  isSubmittingDraft={isSubmittingDraft}
                  isLoading={isLoading}
                  isLegalRepValid={isLegalRepValid}
                  isRecurring={isRecurring}
                  onRecurringChange={setIsRecurring}
                  showSubmitAndCreateNew={!isReferenceDocument}
                  showRecurringToggle={isVisible("recurring")}
                  onSubmit={onSubmit}
                  onSubmitAndCreateNew={onSubmitAndCreateNew}
                  onSaveAsDraft={onSaveAsDraft}
                  methods={methods}
                />
              </Grid>
              {renderBlocks("after_actions")}
            </Grid>
          </Box>
        </Box>
      )}
    </>
  );
};
